import { FormikErrors, FormikHandlers, FormikHelpers, FormikState } from 'formik';
import { ISettingsValues } from 'types';
import { Box } from '@chakra-ui/react';
import { SimpleGrid, FormFileUpload, FormSwitch } from 'components/UI';
import ProductFieldTitle from 'components/common/ProductFieldTitle';
import { validateForm } from 'utils/helpers';
import SettingsFormControl from 'components/Settings/SettingsForm/SettingsFormControl';
import SettingsTextContainer from 'components/Settings/SettingsForm/SettingsTextContainer';
import { FormWysiwyg } from 'components/UI/forms/FormWysiwyg';
import SettingsInput from 'components/Settings/SettingsForm/SettingsInput';
import SettingsFieldDescription from 'components/Settings/SettingsForm/SettingsFieldDescription';

type BrandingFormProps = {
  values: ISettingsValues | Record<string, never>;
  submitted: boolean;
  errors: FormikErrors<ISettingsValues | Record<string, never>>;
  onBlur: FormikHandlers['handleBlur'];
  onChange: FormikHandlers['handleChange'];
  touched: FormikState<ISettingsValues | Record<string, never>>['touched'];
  setFieldValue: FormikHelpers<ISettingsValues | Record<string, never>>['setFieldValue'];
};

const BrandingForm = ({ values, setFieldValue, onChange, onBlur, ...props }: BrandingFormProps) => {
  const formErrors = validateForm<ISettingsValues | Record<string, never>>(props);

  return (
    <Box>
      <SimpleGrid columns={2} spacing={4}>
        <SettingsTextContainer>
          <ProductFieldTitle htmlFor="companyName">COMPANY NAME *</ProductFieldTitle>
          <SettingsFieldDescription>The company using the system.</SettingsFieldDescription>
        </SettingsTextContainer>
        <SettingsFormControl isInvalid={!!formErrors.companyName}>
          <SettingsInput
            name="companyName"
            maxLength={50}
            error={formErrors.companyName}
            value={values.companyName}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Enter the company name"
          />
        </SettingsFormControl>

        <SettingsTextContainer>
          <ProductFieldTitle htmlFor="hideProductNames">HIDE PRODUCT NAMES</ProductFieldTitle>
          <SettingsFieldDescription>
            Controls whether product names are hidden at the top of the configuration options.
          </SettingsFieldDescription>
        </SettingsTextContainer>

        <SettingsFormControl isInvalid={!!formErrors.hideProductNames}>
          <FormSwitch
            name="hideProductNames"
            value={values?.hideProductNames}
            onChange={(e) => setFieldValue('hideProductNames', e.target.checked)}
          />
        </SettingsFormControl>

        <SettingsTextContainer>
          <ProductFieldTitle htmlFor="headerLogo">HEADER/LOADER LOGO *</ProductFieldTitle>
          <SettingsFieldDescription>Logo displayed in the header and viewer.</SettingsFieldDescription>
        </SettingsTextContainer>

        <SettingsFormControl isInvalid={!!formErrors.headerLogo}>
          <FormFileUpload
            error={formErrors.headerLogo}
            name="headerLogo"
            setFieldValue={setFieldValue}
            value={values.headerLogo}
            accept="image/*"
          />
        </SettingsFormControl>

        <SettingsTextContainer>
          <ProductFieldTitle htmlFor="loginLogo">LOGIN LOGO *</ProductFieldTitle>

          <SettingsFieldDescription>Logo displayed when on the login page.</SettingsFieldDescription>
        </SettingsTextContainer>

        <SettingsFormControl isInvalid={!!formErrors.loginLogo}>
          <FormFileUpload
            error={formErrors.loginLogo}
            name="loginLogo"
            setFieldValue={setFieldValue}
            value={values.loginLogo}
            accept="image/*"
          />
        </SettingsFormControl>

        <SettingsTextContainer>
          <ProductFieldTitle htmlFor="loginBackgroundImage">LOGIN BACKGROUND IMAGE</ProductFieldTitle>
          <SettingsFieldDescription>Background image displayed when on the login page.</SettingsFieldDescription>
        </SettingsTextContainer>

        <SettingsFormControl isInvalid={!!formErrors.loginBackgroundImage}>
          <FormFileUpload
            error={formErrors.loginBackgroundImage}
            name="loginBackgroundImage"
            setFieldValue={setFieldValue}
            value={values.loginBackgroundImage}
            accept="image/*"
          />
        </SettingsFormControl>

        <SettingsTextContainer>
          <ProductFieldTitle htmlFor="emailLogo">EMAIL LOGO *</ProductFieldTitle>
          <SettingsFieldDescription>Logo displayed in the emails.</SettingsFieldDescription>
        </SettingsTextContainer>

        <SettingsFormControl isInvalid={!!formErrors.emailLogo}>
          <FormFileUpload
            error={formErrors.emailLogo}
            name="emailLogo"
            setFieldValue={setFieldValue}
            value={values.emailLogo}
            accept="image/*"
          />
        </SettingsFormControl>
      </SimpleGrid>
      <SettingsTextContainer mb={4}>
        <ProductFieldTitle htmlFor="privacyPolicy">PRIVACY POLICY</ProductFieldTitle>

        <SettingsFieldDescription>
          Privacy policy to be displayed at the bottom of the request quotation / order configuration dialog.
        </SettingsFieldDescription>
      </SettingsTextContainer>

      <SettingsFormControl isInvalid={!!formErrors.privacyPolicy}>
        <FormWysiwyg
          name="privacyPolicy"
          error={formErrors.privacyPolicy}
          value={values.privacyPolicy}
          setFieldValue={setFieldValue}
          variant="bare"
          placeholder="Enter the privacy policy."
        />
      </SettingsFormControl>
    </Box>
  );
};

export default BrandingForm;
